import React, { FC, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { MRTTable } from '../../../components';
import { useToastContext } from '../../../hooks';
import { IApplication, IDropdownApplications, ApplicationDefaults, ILicense, IUser } from '../../../models/smp';
import { DeleteOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { Routes } from '../../../constants';
import { AddEditApplicationModal } from './include/modals';
import { SMPAPI } from '../../../fetch/smp';
import { ApplicationDetails } from './ApplicationDetails';
import { ToastV3 } from '../components/ToastV3';
import {ConfirmDeleteModal} from '../components';


interface RouteParams {
  applicationId?: string;
}

const Applications: FC = () => {
  const { applicationId } = useParams<RouteParams>();
  const history = useHistory();
  const { showToast } = useToastContext();

  // Detail view state
  const [application, setApplication] = useState<IApplication>(ApplicationDefaults.getDefault());

  // Listing state
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [applications, setApplications] = useState<IApplication[]>([]);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  
  // MRT states
  const [mrtSorting, setMrtSorting] = useState<any[]>([]);
  const [mrtColumnFilters, setMrtColumnFilters] = useState<any[]>([]);
  const [mrtGlobalFilter, setMrtGlobalFilter] = useState<string>('');
  const mrtSearchDebounceRef = useRef<any>(null);
  
  const showToastRef = useRef(showToast);
  useEffect(() => { showToastRef.current = showToast }, [showToast]);

  // Cleanup search debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (mrtSearchDebounceRef.current) {
        clearTimeout(mrtSearchDebounceRef.current);
      }
    };
  }, []);

  // Persist MRT state (sorting/filters/page) in localStorage
  const storageKey = 'smp:applications:mrt';

  // Restore persisted MRT state safely
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const s = JSON.parse(raw);
        if (Array.isArray(s?.sorting)) setMrtSorting(s.sorting);
        if (Array.isArray(s?.columnFilters)) setMrtColumnFilters(s.columnFilters);
        if (typeof s?.globalFilter === 'string') setMrtGlobalFilter(s.globalFilter);
        if (typeof s?.page === 'number' && s.page >= 0) setPage(s.page);
        if (typeof s?.perPage === 'number' && s.perPage > 0) setPerPage(s.perPage);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const s = {
      sorting: mrtSorting,
      columnFilters: mrtColumnFilters,
      globalFilter: mrtGlobalFilter,
      page,
      perPage,
    };
    try { localStorage.setItem(storageKey, JSON.stringify(s)); } catch {}
  }, [mrtSorting, mrtColumnFilters, mrtGlobalFilter, page, perPage]);

  const [applicationTypes, setApplicationTypes] = useState<IDropdownApplications[]>([]);
  const [editingApplication, setEditingApplication] = useState<IApplication | undefined>(undefined);
  const [categories, setCategories] = useState<IDropdownApplications[]>([]);

  // Delete confirmation state
  const [pendingDeleteApp, setPendingDeleteApp] = useState<IApplication | null>(null);
  const [pendingUsers, setPendingUsers] = useState<IUser[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingLicenses, setPendingLicenses] = useState<ILicense[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadingDependencies, setLoadingDependencies] = useState(false);

  // Load single application detail
  useEffect(() => {
    if (applicationId) {
      SMPAPI.getApplication(applicationId).then(setApplication);
    }
  }, [applicationId]);

  // Build server query params from current UI/MRT state
  const buildApplicationQueryParams = useCallback((opts?: { page?: number; perPage?: number }) => {
    // Build server-side sort param from MRT sorting state for global sorting across pages
    const allowedSortIds = new Set([
      'name',
      'typeName', 
      'status',
      'categoryName',
      'vendorName',
      'createdDate',
    ]);
    const sortString = (Array.isArray(mrtSorting) ? mrtSorting : [])
      .map((s: any) => {
        const id = s.id || s.columnId || s.accessorKey;
        if (!id || !allowedSortIds.has(id)) return null;
        const dir = s.desc ? 'desc' : 'asc';
        return `${id}:${dir}`;
      })
      .filter(Boolean)
      .join(',');

    const pageIdx = opts?.page ?? (page + 1);
    const pageSize = opts?.perPage ?? perPage;
    
    // Build search from column filters and global filter
    const columnSearchTerms = (Array.isArray(mrtColumnFilters) ? mrtColumnFilters : [])
      .map((filter: any) => filter.value)
      .filter(Boolean);
    const globalSearchTerm = mrtGlobalFilter ? [mrtGlobalFilter] : [];
    const allSearchTerms = [...columnSearchTerms, ...globalSearchTerm];
    const searchString = allSearchTerms.length > 0 ? allSearchTerms.join(' ') : undefined;
    
    return {
      page: pageIdx,
      perPage: pageSize,
      search: searchString,
      sort: sortString || undefined,
    };
  }, [mrtSorting, mrtColumnFilters, mrtGlobalFilter, page, perPage]);

  // Listing
  const loadApplications = useCallback(async () => {
    setLoadingApplications(true);
    try {
      const params = buildApplicationQueryParams();
      const data = await SMPAPI.fetchApplications(params);
      // Calculate the starting row number based on current page and page size
      const startIndex = page * perPage;
      setApplications(
        data.records.map((rec: any, i: number) => ({
          ...rec,
          rowCount: startIndex + i + 1,
        }))
      );
      setTotal(data.totalRecordCount);
    } catch (error: any) {
      showToast('error', error?.detail || 'Error retrieving applications');
    } finally {
      setLoadingApplications(false);
    }
  }, [buildApplicationQueryParams, page, perPage, showToast]);

  useEffect(() => {
    if (!applicationId) {
      loadApplications();
    }
  }, [applicationId, loadApplications]);

  // Reload when MRT state changes (sorting, filtering)
  useEffect(() => {
    if (!applicationId) {
      loadApplications();
    }
  }, [applicationId, mrtSorting, mrtColumnFilters, mrtGlobalFilter, page, perPage, loadApplications]);

  useEffect(() => {
    SMPAPI.fetchApplicationTypes().then(setApplicationTypes);
    SMPAPI.fetchCategories().then(setCategories);
  }, []);

  const handleDeleteApplication = async (app: IApplication) => {
    if (!app.applicationID) return;
    try {
      await SMPAPI.deleteApplication(app.applicationID);
      showToast('success', 'Application deleted');
      await loadApplications();
    } catch {
      showToast('error', 'Failed to delete application');
    }
  };


  const requestDelete = useCallback(async (app: IApplication) => {
    setPendingDeleteApp(app);
    setLoadingDependencies(true);
    try {
      const [licenseRes, userRes] = await Promise.all([
        SMPAPI.fetchLicenses(app.applicationID!, { page: 1, perPage: 999 }),
        SMPAPI.fetchUsers(app.applicationID!, { page: 1, perPage: 999 }),
      ]);
      setPendingLicenses(licenseRes.records);
      setPendingUsers(userRes.records);
      setLoadingDependencies(false);
      if (licenseRes.records.length > 0 || userRes.records.length > 0) {
        setShowDeleteModal(true);
      } else {
        setShowDeleteConfirm(true);
      }
    } catch {
      showToast('error', 'Failed to load dependencies');
      setLoadingDependencies(false);
    }
  }, [showToast]);

  

  const handleSaveApplication = async (updatedApp: IApplication) => {
    const isNew = !updatedApp.applicationID;

    try {
      const payload = {
        ...updatedApp,
        CreatedAt: updatedApp.CreatedAt || new Date().toISOString(),
        Name: updatedApp.name,
        TypeID: updatedApp.TypeID,
        Description: updatedApp.Description,
        Website: updatedApp.Website,
        CategoryID: updatedApp.CategoryID,
        VendorID: updatedApp.VendorID,
        LogInCredentialsRequired: updatedApp.LogInCredentialsRequired,
        SingleSignOn: updatedApp.SingleSignOn,
        SharepointLink: updatedApp.SharepointLink
      };

      const savedApp = await SMPAPI.upsertApplication(payload);

      showToast('success', 'Application saved');

      if (isNew && savedApp.applicationID) {
        window.open(Routes.viewApplication(savedApp.applicationID), '_blank');
      }

      await loadApplications();
    } catch {
      showToast('error', 'Failed to save application');
    } finally {
      setEditingApplication(undefined);
    }
  };

  const reloadDependencies = useCallback(async () => {
    if (!pendingDeleteApp) return;
    setLoadingDependencies(true);
    try {
      const [licenseRes, userRes] = await Promise.all([
        SMPAPI.fetchLicenses(pendingDeleteApp.applicationID!, { page: 1, perPage: 999 }),
        SMPAPI.fetchUsers(pendingDeleteApp.applicationID!, { page: 1, perPage: 999 }),
      ]);
      setPendingLicenses(licenseRes.records);
      setPendingUsers(userRes.records);
    } catch {
      showToast('error', 'Failed to load dependencies');
    } finally {
      setLoadingDependencies(false);
    }
  }, [pendingDeleteApp, showToast]);

    // Dependency columns for ConfirmDeleteModal
    const licenseColumns = useMemo(() => [
      { Header: 'Name', accessor: 'licenseName' },
      { Header: 'Type', accessor: 'licenseType' },
      { Header: 'Status', accessor: 'licenseStatus' },
    ], []);
  
    const userColumns = useMemo(() => [
      { Header: 'User Name', accessor: 'userName' },
      { Header: 'Email', accessor: 'email' },
      { Header: 'Employment Status', accessor: 'employmentStatus' },
    ], []);



// MRT columns (memoized) - avoid remounts that collapse the search field
  const mrtColumns = useMemo(() => {
    const unique = (arr: any[], key: string) => Array.from(new Set(arr.map(x => (x?.[key] ?? '').toString()).filter(Boolean))).sort();
    const typeOptions = unique(applications, 'typeName');
    const statusOptions = unique(applications, 'status');
    const categoryOptions = unique(applications, 'categoryName');

    const ellipsisCell = (text: any, maxWidth: number = 250) => (
      <Tooltip title={text ?? ''} arrow>
        <Box component="span" sx={{ display: 'inline-block', maxWidth, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{text ?? ''}</Box>
      </Tooltip>
    );

    return [
      {
        accessorKey: 'rowCount',
        header: '#',
        size: 30,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }: any) => row.original.rowCount,
      },
      {
        accessorKey: 'name',
        header: 'Application Name',
        filterVariant: 'text',
        size: 300,
        muiTableBodyCellProps: { sx: { maxWidth: 300 } },
        Cell: ({ row }: any) => (
          <Tooltip title={row.original.name} arrow>
            <Button
              variant="text"
              onClick={() => history.push(Routes.viewApplication(row.original.applicationID))}
              sx={{ 
                p: 0, 
                textTransform: 'none', 
                textDecoration: 'underline', 
                minWidth: 0, 
                fontWeight: 500, 
                color: '#1976d2', 
                cursor: 'pointer', 
                fontSize: 'inherit',
                maxWidth: 300,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'left',
                justifyContent: 'flex-start',
              }}
            >
              {row.original.name}
            </Button>
          </Tooltip>
        ),
      },
      { 
        accessorKey: 'typeName', 
        header: 'Type', 
        filterVariant: 'select', 
        filterSelectOptions: typeOptions, 
        size: 140, 
        Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 140) 
      },
      { 
        accessorKey: 'status', 
        header: 'Status', 
        filterVariant: 'select', 
        filterSelectOptions: statusOptions, 
        size: 120, 
        Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 120) 
      },
      { 
        accessorKey: 'categoryName', 
        header: 'Category', 
        filterVariant: 'select', 
        filterSelectOptions: categoryOptions, 
        size: 150, 
        Cell: ({ cell }: any) => ellipsisCell(cell.getValue(), 150) 
      },
      { 
        id: 'actions', 
        header: 'Delete', 
        size: 80, 
        enableColumnFilter: false, 
        enableSorting: false, 
        Cell: ({ row }: any) => {
          const app = row.original;
          if (!app.applicationID) return <Typography variant="caption">N/A</Typography>;
          return (
            <Box>
              <IconButton size="small" color="error" onClick={() => requestDelete(app)}>
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </Box>
          );
        } 
      },
    ];
  }, [applications, history, requestDelete]);




  // Listing view
  if (!applicationId) {
    return (
      <Box sx={{ backgroundColor: '#f0f0f0', p: 2, borderRadius: 2 }}>
        {/* Top: Add Button */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setEditingApplication(ApplicationDefaults.getDefault())}
          >
            Add Application
          </Button>
        </Box>

        <Box
          sx={{
            maxWidth: '100%',
            overflowX: 'auto',
            position: 'relative',
            minHeight: 100,
          }}
        >
          <Box
            sx={{
              minWidth: '100%',
              overflow: 'auto',
              border: '1px solid #e0e0e0', 
              borderRadius: '8px',           
              background: 'white',          
              '& table': {
                width: '100%',
                tableLayout: 'auto',
              },
              '& .MuiTableCell-root': {
                fontSize: '0.75rem',
                padding: '6px 8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '250px', 
                borderBottom: '1px solid rgb(212, 210, 210)', 
              },
              '& .MuiTableCell-head': {
                fontWeight: 'bold',
                whiteSpace: 'normal',
                overflowWrap: 'normal',
                wordWrap: 'normal',
                wordBreak: 'keep-all',
                hyphens: 'manual',
                maxWidth: '150px',
                transition: 'color 0.3s ease',
                borderBottom: '2px solid rgb(212, 210, 210)',
              },
              '& .MuiTableCell-head:hover, & .MuiTableCell-head:hover *': {
                color: '#64b5f6',
                cursor: 'pointer',
              },
              '& th:last-of-type, & td:last-of-type': {
                position: 'sticky',
                right: 0,
                zIndex: 2,
                textAlign: 'center ! important',
              },
              'td:last-of-type': {
                p: 0,
                backgroundColor: '#fff',
              },
              '& .MuiTableCell-body': { py: 0.1 },
              '&::-webkit-scrollbar': { height: 2 },
              '&::-webkit-scrollbar-track': { background: '#e0e0e0' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#a0a0a0',
                borderRadius: 3,
              },
              scrollbarHeight: 'thin',
              scrollbarColor: '#fff #fff',
            }}
          >
            <MRTTable
              columns={mrtColumns as any}
              data={applications as any}
              loading={loadingApplications}
              manualPagination
              manualSorting
              totalRowCount={total}
              pageIndex={page}
              pageSize={perPage}
              onPaginationChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater({ pageIndex: page, pageSize: perPage }) : updater;
                if (typeof next?.pageIndex === 'number') setPage(next.pageIndex);
                if (typeof next?.pageSize === 'number') setPerPage(next.pageSize);
              }}
              onSortingChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater(mrtSorting) : updater;
                setMrtSorting(next || []);
                // Reset to first page so server can repaginate after sort changes
                setPage(0);
              }}
              sorting={mrtSorting}
              columnFilters={mrtColumnFilters}
              onColumnFiltersChange={(updater: any) => {
                const next = typeof updater === 'function' ? updater(mrtColumnFilters) : updater;
                setMrtColumnFilters(next || []);
                // Reset to first page when filters change to search entire dataset
                setPage(0);
              }}
              globalFilter={mrtGlobalFilter}
              onGlobalFilterChange={(val: any) => {
                const v = typeof val === 'string' ? val : '';
                setMrtGlobalFilter(v);
                // Debounce the search to prevent too many API calls
                if (mrtSearchDebounceRef.current) {
                  clearTimeout(mrtSearchDebounceRef.current);
                }
                mrtSearchDebounceRef.current = setTimeout(() => {
                  if (v.trim().length >= 2 || v.trim().length === 0) {
                    // Reset to first page when searching to search entire dataset
                    setPage(0);
                  }
                }, 300);
              }}
            />
          </Box>
        </Box>

        <AddEditApplicationModal
          open={!!editingApplication}
          application={editingApplication}
          applicationTypes={applicationTypes}
          categories={categories}
          onClose={() => setEditingApplication(undefined)}
          onSave={handleSaveApplication}
        />

        {/* ToastV3 fallback */}
        {showDeleteConfirm && (
          <ToastV3
            type="error"
            showReset={false}
            message="Are you sure you want to delete this application?"
            confirmText="Delete"
            onCancel={() => setShowDeleteConfirm(false)}
            onSave={async () => {
              await handleDeleteApplication(pendingDeleteApp!);
              setShowDeleteConfirm(false);
            }}
            onClose={() => setShowDeleteConfirm(false)}
          />
        )}

        {/* ConfirmDeleteModal */}
        {pendingDeleteApp && (
          <ConfirmDeleteModal<ILicense | IUser>
            open={showDeleteModal}
            title={`Cannot delete application: ${pendingDeleteApp?.name}`}
            message="The following licenses and users are tied to this application. Delete or unlink them first."
            columns={pendingLicenses.length > 0 ? licenseColumns : userColumns}
            data={pendingLicenses.length > 0 ? pendingLicenses : pendingUsers}
            isLoading={loadingDependencies}
            onDeleteItem={async (item) => {
              if ('licenseID' in item) {
                await SMPAPI.deleteLicense(item.licenseID!);
              } else if ('applicationUserID' in item) {
                await SMPAPI.deleteUser(item.applicationUserID!);
              }
              await reloadDependencies();
            }}
            onUnlinkItem={async (item) => {
              if (!pendingDeleteApp) return;
              if ('licenseID' in item) {
                await SMPAPI.detachLicense(pendingDeleteApp.applicationID!, Number(item.licenseID));
              } else if ('applicationUserID' in item) {
                await SMPAPI.detachUser(pendingDeleteApp.applicationID!, item.applicationUserID!);
              }
              await reloadDependencies();
            }}
            onDeleteAllItems={async () => {
              await Promise.all([
                ...pendingLicenses.map(l => SMPAPI.deleteLicense(l.licenseID!)),
                ...pendingUsers.map(u => SMPAPI.deleteUser(u.applicationUserID!)),
              ]);
              await reloadDependencies();
            }}
            onConfirm={async () => {
              await handleDeleteApplication(pendingDeleteApp!);
              setShowDeleteModal(false);
            }}
            onClose={() => setShowDeleteModal(false)}
            confirmText="Delete Application"
            deleteAllText="Delete all linked Licenses and Users"
          />
        )}

      </Box>
    );
  }

  // Detail view
  return <ApplicationDetails application={application} />;
};

export default Applications;
